import React, { useCallback, useEffect, useMemo, useState } from "react";
import Selector from "./Selector/Selector";
import type { DatePickerProps, DatePreset, DateRangeValue } from "./types";
import { Wrapper, Toggle } from "./styles";

// ─── helpers ──────────────────────────────────────────────────────────────

const DEFAULT_TITLE = "No dates selected";

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const formatDisplayDate = (d: Date): string =>
  new Intl.DateTimeFormat("en", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  }).format(d);

const isMatchingPreset = (preset: DatePreset, value: DateRangeValue): boolean => {
  const [start, end] = value;
  if (!start || !end) return false;
  return isSameDay(preset.start, start) && isSameDay(preset.end, end);
};

const formatRangeTitle = (range: DateRangeValue): string => {
  const [start, end] = range;
  if (!start || !end) return DEFAULT_TITLE;
  return `${formatDisplayDate(start)} – ${formatDisplayDate(end)}`;
};

// ─── component ────────────────────────────────────────────────────────────

const DatePicker = React.forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      onChange,
      value: controlledValue,
      presets,
      style,
      closeDelay,
      highlightCol,
      defaultTitle,
      menuPlacement = "left",
    },
    ref
  ) => {
    const [selectOpen, setSelectOpen] = useState(false);
    const [title, setTitle] = useState<string>(defaultTitle ?? DEFAULT_TITLE);

    /**
     * Internal value — source of truth for what to restore in Selector.
     * Updated on every confirmed selection so highlights survive popup reopen
     * even when the parent does not pass a controlled `value` prop.
     */
    const [internalValue, setInternalValue] = useState<DateRangeValue>(
      controlledValue ?? [null, null]
    );

    const presetLookup = useMemo(() => presets ?? [], [presets]);

    // ── title sync ──────────────────────────────────────────────────────
    const updateTitle = useCallback(
      (range: DateRangeValue | null) => {
        if (!range || !range[0] || !range[1]) {
          setTitle(defaultTitle ?? DEFAULT_TITLE);
          return;
        }
        const matching = presetLookup.find((p) => isMatchingPreset(p, range));
        setTitle(matching ? matching.title : formatRangeTitle(range));
      },
      [defaultTitle, presetLookup]
    );

    // Sync when controlled value changes from outside
    useEffect(() => {
      if (controlledValue) {
        setInternalValue(controlledValue);
        updateTitle(controlledValue);
      }
    }, [controlledValue, updateTitle]);

    // ── on range confirmed ──────────────────────────────────────────────
    const handleChange = useCallback(
      (range: DateRangeValue) => {
        setInternalValue(range);   // persist so Selector can restore on reopen
        updateTitle(range);

        if (typeof onChange === "function") {
          onChange(range);
        }

        const delay = typeof closeDelay === "number" ? closeDelay : 300;
        setTimeout(() => setSelectOpen(false), delay);
      },
      [onChange, updateTitle, closeDelay]
    );

    return (
      <Wrapper ref={ref} style={style}>
        <Toggle
          type="button"
          onClick={() => setSelectOpen((prev) => !prev)}
          aria-haspopup="dialog"
          aria-expanded={selectOpen}
        >
          <span>{title}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            aria-hidden="true"
          >
            <path fill="rgba(0,0,0,.5)" d="M7,10l5,5,5-5H7Z" />
          </svg>
        </Toggle>

        {selectOpen && (
          <Selector
            presets={presetLookup}
            onChange={handleChange}
            highlightCol={highlightCol}
            menuPlacement={menuPlacement}
            initialValue={internalValue}
          />
        )}
      </Wrapper>
    );
  }
);

DatePicker.displayName = "DatePicker";

export default DatePicker;
